import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { api } from "#/lib/api";
import type {
  AIResponse,
  ContinueSessionRequest,
  ContinueSessionResponse,
  CreateSessionResponse,
  SessionStartError,
  Session,
} from "@myapp/shared";

export class SessionLimitReachedError extends Error {
  nextFreeAt?: string;

  constructor(nextFreeAt?: string) {
    super("limit_reached");
    this.name = "SessionLimitReachedError";
    this.nextFreeAt = nextFreeAt;
  }
}

export class SessionDailyCapError extends Error {
  constructor() {
    super("daily_cap");
    this.name = "SessionDailyCapError";
  }
}

export async function createSession(): Promise<CreateSessionResponse> {
  try {
    const { data } = await api.post<CreateSessionResponse>("/sessions");
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 402) {
      const body = err.response.data as SessionStartError;
      if (body.error === "daily_cap") {
        throw new SessionDailyCapError();
      }
      throw new SessionLimitReachedError(body.nextFreeAt);
    }
    throw err;
  }
}

export async function continueSession(
  sessionId: string,
  input: ContinueSessionRequest,
): Promise<ContinueSessionResponse> {
  const { data } = await api.post<ContinueSessionResponse>(
    `/sessions/${sessionId}/continue`,
    input,
  );
  return data;
}

export async function fetchSession(sessionId: string): Promise<Session> {
  const { data } = await api.get<{ session: Session }>(`/sessions/${sessionId}`);
  return data.session;
}

export function useCreateSession() {
  return useMutation<CreateSessionResponse, Error>({
    mutationFn: createSession,
  });
}

export function useContinueSession(sessionId: string | null) {
  return useMutation<
    ContinueSessionResponse,
    Error,
    { response: string; onSuccess?: (data: ContinueSessionResponse) => void }
  >({
    mutationFn: ({ response }) => {
      if (!sessionId) throw new Error("No active session");
      return continueSession(sessionId, { response });
    },
  });
}

export type { AIResponse, Session };
